/**
 * @fileoverview Onboarding Component
 *
 * This file implements the main onboarding workflow container for the user registration process.
 * It manages the multi-step onboarding flow including Account, Role, Company, and Personal information.
 * 
 * Key features:
 * - Vertical stepper navigation between onboarding steps
 * - Dynamic form field configuration based on client settings
 * - Data fetching for dropdown options
 * - Profile completion validation
 * - Conditional field visibility based on country selection
 * - Client-specific branding (logo and background images)
 */

import React, { useContext, useEffect, useState } from 'react';
import Header from '../../commons/Header';
import Account from './Account';
import Role from './Role';
import Footer from '../../commons/Footer';
import Company from './Company';
import Personal from './Personal';

import { globalContext } from '../../store';
import Api from '../../Api';
import { checkDropDownValues, checkAuthErr, checkUnauthorized, filterFields, hasAnyDemographicFieldEnabled, getBrandValues, getBrandImages, dispatchToGlobalState } from '../../commons/commonFunctions';
import Loader from '../../commons/Loader';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import { clientInfo } from '../../commons/ClientDynamicInfo';

/**
 * Onboarding Component
 * 
 * Main container component that orchestrates the multi-step onboarding process.
 * Handles data loading, form configuration, and step navigation.
 * 
 * @returns {JSX.Element} The onboarding container with stepper navigation
 */
const Onboarding = () => {
  const navigate = useNavigate();
  const { globalState, dispatch } = useContext(globalContext);
  const { pathname } = useLocation();
  const title = globalState?.clientData?.name;
  const clientSurveyData = globalState?.clientData?.demographicSurveyData;

  // State variables for job-related dropdown options
  const [jobLevelOptions, setJobLevelOptions] = useState([]);
  const [yearsAtJobLevelOptions, setYearsAtJobLevelOptions] = useState([]);
  const [yearsInLeadershipRoleOptions, setYearsInLeadershipRoleOptions] = useState([]);
  const [directReportsOptions, setDirectReportsOptions] = useState([]);
  const [orgSizeUnderLeaderOptions, setOrgSizeUnderLeaderOptions] = useState([]);
  const [jobFunctionOptions, setJobFunctionOptions] = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);

  // State variables for company-related dropdown options
  const [orgTypeOptions, setOrgTypeOptions] = useState([]);
  const [orgStructureOptions, setOrgStructureOptions] = useState([]);
  const [orgSizeOptions, setOrgSizeOptions] = useState([]);
  const [orgCycleOptions, setOrgCycleOptions] = useState([]);
  const [orgRevenueOptions, setOrgRevenueOptions] = useState([]);
  const [workingEnvironmentOptions, setWorkingEnvironmentOptions] = useState([]);
  const [showLoader, setShowLoader] = useState(true);

  // State variables for personal demographic dropdown options
  const [countryOfResidenceOptions, setCountryOfResidenceOptions] = useState('');
  const [genderOptions, setGenderOptions] = useState('');
  const [ethnicityOptions, setethnicityOptions] = useState('');
  const [lgbtqOptions, setLgbtqOptions] = useState('');
  const [diversityNonSpecificOptions, setDiversityNonSpecificOptions] = useState('');
  const [veteranStatusOptions, setVeteranStatusOptions] = useState('');
  const [educationOptions, setEducationOptions] = useState('');
  const [ageOptions, setAgeOptions] = useState('');

  // State variables for client branding
  const [headerLogo, setHeaderLogo] = useState<string>('');
  const [leftPanelImg, setLeftPanelImg] = useState<string>('');
  const [primaryColor, setPrimaryColor] = useState<string>('#BA3A19');

  // Get session data from browser storage
  let loginToken: any = JSON.parse(sessionStorage.getItem('loginToken') ?? 'false');
  let clientId: any = JSON.parse(sessionStorage.getItem('clientId') ?? 'false');

  // Flags to prevent duplicate API calls
  let fetchLeaderData = false;
  let fetchLookupData = false;
  
  /**
   * Effect hook to initialize data and check profile completion
   * 
   * On component mount:
   * 1. Fetches leader profile data if not already in state
   * 2. Redirects to dashboard if profile is already completed
   * 3. Loads client-specific branding (logo and background image)
   */

  const handleLeaderAndLookup = () => {
    if (
      Object.keys(globalState?.leaderData).length === 0 ||
      Object.keys(globalState?.lookUpData).length === 0
    ) {
      getLeaderData();
    } else {
      if (globalState?.leaderData?.isProfileCompleted) {
        navigate('/dashboard');
      }
      setShowLoader(false);
    }
  };

  const handleBrandingFallback = () => {
    const headerLogoId = getBrandValues(globalState?.clientData?.branding, 'COMPANY_LOGO');
    const leftPanelImgId = getBrandValues(globalState?.clientData?.branding, 'WELCOME_PAGE_IMAGE');

    if (globalState?.leftPanelImg) {
      setLeftPanelImg(globalState.leftPanelImg);
    } else {
      getBrandImages(leftPanelImgId, setLeftPanelImg, 'leftImage', dispatch, 'SET_LEFT_PANEL_IMG');
    }

    if (globalState?.headerLogo) {
      setHeaderLogo(globalState.headerLogo);
    } else {
      getBrandImages(headerLogoId, setHeaderLogo, 'headerLogo', dispatch, 'SET_HEADER_LOGO');
    }

    if (globalState?.primaryColor) {
      setPrimaryColor(globalState.primaryColor);
    } else {
      const primColor =
        getBrandValues(globalState?.clientData?.branding, 'PRIMARY_COLOR')?.value || '#BA3A19';
        dispatchToGlobalState(setPrimaryColor, primColor, dispatch, 'SET_PRIMARY_COLOR');
    }
  };

  useEffect(() => {
    handleLeaderAndLookup();
    if (title && clientInfo.hasOwnProperty(title)) {
      const info = clientInfo[title];
      setLeftPanelImg(info.leftImage);
      setHeaderLogo(info.headerLogo);
    } else {
      handleBrandingFallback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /**
   * Effect hook to scroll to top when route changes
   * 
   * Ensures the page scrolls to the top whenever the user
   * navigates between onboarding steps.
   */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  /**
   * Prepares dropdown sub-list options from API response
   * 
   * Transforms nested dropdown options (like industry sectors for an industry)
   * into a format compatible with the Select component, adding a default "Select" option.
   * 
   * @param {Object} i - Object containing sub-list entries
   * @returns {Array|undefined} Array of sub-list options or undefined if none exist
   */
  const subListPrepared = (i: any) => {
    if (i?.dsSubList && i?.dsSubList.length > 0) {
      const subList = i?.dsSubList?.map((list: any) => {
        return {
          text: list.dsSubListEntry,
          value: list.dsSubListEntry,
        };
      });
      subList.unshift({ text: 'Select', value: '' });
      return subList;
    }
  };
  /**
   * Prepares main dropdown list options from API response
   * 
   * Transforms API response data into a format compatible with the Select
   * component, including any nested sub-lists, and adds a default "Select" option.
   * 
   * @param {Object} item - Object containing list entries
   * @returns {Array} Array of dropdown options
   */
  const listPrepared = (item: any) => {
    const listWithSelect = item?.dsListEntries?.map((i: any) => {
      return {
        text: i.dsListEntry,
        value: i.dsListEntry,
        subList: subListPrepared(i),
      };
    });
    listWithSelect.unshift({ text: 'Select', value: '' });
    return listWithSelect;
  };

  /**
   * Fetches dropdown lookup data from API
   * 
   * Retrieves all dropdown options for the demographic survey
   * from the backend and formats them for use in form dropdowns.
   */
  const getLookUpData = () => {
    const payload = {
      clientId: clientId,
    };
    if (fetchLookupData === false) {
      fetchLookupData = true;
      Api.getLookUp(payload)
        .then((response: any) => {
          if (response.success) {
            const responseData = response?.response?.data?.data?.[0]?.demographicSurveyLookups;
            const newData = responseData?.map((item: any) => {
              return {
                dropDownName: item.dsListName,
                dropDownValues: listPrepared(item),
              };
            });
            dispatch({
              type: 'SET_LOOKUP_DATA',
              payload: newData,
            });
            setShowLoader(false);
          } else {
            setShowLoader(false);
            checkUnauthorized(response, dispatch);
          }
        })
        .catch((err) => {
          setShowLoader(false);
          checkAuthErr(err, dispatch);
        });
    }
  };
  /**
   * Fetches leader profile data from API
   * 
   * Retrieves the user's profile data from the backend based on their
   * email and client ID. If successful, it then fetches the lookup data
   * for form dropdowns. Redirects to dashboard if profile is already completed.
   */
  const getLeaderData = () => {
    const payload = {
      email: loginToken?.idToken?.claims?.email.toLowerCase(),
      clientId: clientId,
    };

    if (fetchLeaderData === false) {
      fetchLeaderData = true;
      if (Object.keys(globalState?.leaderData).length === 0) {
        Api.getLeaderProfile(payload)
          .then((response: any) => {
            if (response.success) {
              const profileData = response?.response?.data?.data?.[0];
              dispatch({
                type: 'SET_LEADER_DATA',
                payload: profileData,
              });
              if (profileData?.isProfileCompleted) {
                navigate('/dashboard');
              }
              getLookUpData();
            } else {
              setShowLoader(false);
              checkUnauthorized(response, dispatch);
            }
          })
          .catch((err) => {
            setShowLoader(false);
            checkAuthErr(err, dispatch);
          });
      } else {
        getLookUpData();
      }
    }
  };
  /**
   * Effect hook to populate dropdown options from global state
   * 
   * When lookup data is available in global state, extracts and sets
   * all the dropdown options needed for the onboarding forms.
   */
  useEffect(() => {
    setJobLevelOptions(checkDropDownValues('Job Level', globalState?.lookUpData));
    setYearsAtJobLevelOptions(checkDropDownValues('Years at Job Level', globalState?.lookUpData));
    setYearsInLeadershipRoleOptions(checkDropDownValues('Years in Leadership Role', globalState?.lookUpData));
    setDirectReportsOptions(checkDropDownValues('Direct Reports', globalState?.lookUpData));
    setOrgSizeUnderLeaderOptions(checkDropDownValues('Org Size Under Leadership', globalState?.lookUpData));
    setJobFunctionOptions(checkDropDownValues('Job Function', globalState?.lookUpData));
    setIndustryOptions(checkDropDownValues('Industry', globalState?.lookUpData));
    setOrgTypeOptions(checkDropDownValues('Org Type', globalState?.lookUpData));
    setOrgStructureOptions(checkDropDownValues('Org Structure', globalState?.lookUpData));
    setOrgCycleOptions(checkDropDownValues('Cycle/Org Cycle', globalState?.lookUpData));
    setOrgSizeOptions(checkDropDownValues('Org Size', globalState?.lookUpData));
    setOrgRevenueOptions(checkDropDownValues('Revenue', globalState?.lookUpData));
    setWorkingEnvironmentOptions(checkDropDownValues('Working Environment', globalState?.lookUpData));
    setCountryOfResidenceOptions(checkDropDownValues('Country of Residence', globalState?.lookUpData));
    setGenderOptions(checkDropDownValues('Gender', globalState?.lookUpData));
    setLgbtqOptions(checkDropDownValues('LGBTQ', globalState?.lookUpData));
    setDiversityNonSpecificOptions(checkDropDownValues('Diversity, Non-Specific', globalState?.lookUpData));
    setVeteranStatusOptions(checkDropDownValues('Veteran Status', globalState?.lookUpData));
    setEducationOptions(checkDropDownValues('Education', globalState?.lookUpData));
    setAgeOptions(checkDropDownValues('Age', globalState?.lookUpData));
    setethnicityOptions(checkDropDownValues('Ethnicity', globalState?.lookUpData));
  }, [globalState?.lookUpData]);

  /**
   * Determines if a field should be shown based on client configuration
   * 
   * Used to control which demographic fields are displayed in the onboarding
   * forms based on client-specific settings.
   * 
   * @param {boolean} key - Client configuration value for the field
   * @returns {boolean} Whether the field should be displayed
   */
  const checkShowFields = (key: any) => {
    // If no client survey data is loaded, default to showing all fields
    if (!clientSurveyData || Object.keys(clientSurveyData).length === 0) {
      return true;
    }
    // If client survey data exists, only show field if explicitly set to true
    // Return false if key is false, undefined, or null
    return key === true;
  };

  /**
   * Checks if at least one field in showFields object is true
   * 
   * Used to determine if a step should be visible. If all fields are false,
   * the entire step should be hidden from the onboarding flow.
   * 
   * @param {Object} showFields - Object containing field visibility flags
   * @returns {boolean} True if at least one field should be shown, false otherwise
   */
  const hasAnyVisibleFields = (showFields: any) => {
    return Object.values(showFields).some((value) => value === true);
  };
  /**
   * Account step props configuration
   * 
   * Collects all the props needed for the Account component,
   * including pre-filled data from the leader profile.
   */
  // Check if any demographic fields are enabled for this client
  let anyDemographicsEnabled = hasAnyDemographicFieldEnabled(clientSurveyData);

  const accountProps = {
    firstName: globalState?.leaderData?.firstName,
    lastName: globalState?.leaderData?.lastName,
    email: globalState?.leaderData?.email,
    isPrivacyPolicyAccepted: globalState?.leaderData?.applications?.leaderPortal?.isPrivacyPolicyAccepted,
    leaderProfileId: globalState?.leaderData?.id,
    anyDemographicsEnabled,
  };

  /**
   * Role step props configuration
   * 
   * Collects all the props needed for the Role component,
   * including pre-filled data and dropdown options.
   */
  const roleShowFields = {
    showJobTitle: checkShowFields(clientSurveyData?.jobTitle),
    showJobLevel: checkShowFields(clientSurveyData?.jobLevel),
    showJobFunction: checkShowFields(clientSurveyData?.jobFunction),
    showJobSubFunction: checkShowFields(clientSurveyData?.jobSubFunction),
    showHireDate: checkShowFields(clientSurveyData?.hireDate),
    showTerminationDate: checkShowFields(clientSurveyData?.terminationDate),
    showYearsAtJobLevel: checkShowFields(clientSurveyData?.yearsAtJobLevel),
    showYearsInLeadershipRole: checkShowFields(clientSurveyData?.yearsInLeadershipRole),
    showDirectReports: checkShowFields(clientSurveyData?.directReports),
    showOrgSizeUnderLeadership: checkShowFields(clientSurveyData?.orgSizeUnderLeader),
  };

  const isRoleStepVisible = hasAnyVisibleFields(roleShowFields);


  const roleProps = {
    jobTitle: globalState?.leaderData?.demographicSurveyData?.jobTitle,
    hireDate: globalState?.leaderData?.demographicSurveyData?.hireDate,
    jobRole: globalState?.leaderData?.demographicSurveyData?.jobRole,
    jobLevel: globalState?.leaderData?.demographicSurveyData?.jobLevel,
    jobFunction: globalState?.leaderData?.demographicSurveyData?.jobFunction,
    jobSubFunction: globalState?.leaderData?.demographicSurveyData?.jobSubFunction,
    yearsAtJobLevel: globalState?.leaderData?.demographicSurveyData?.yearsAtJobLevel,
    yearsInLeadershipRole: globalState?.leaderData?.demographicSurveyData?.yearsInLeadershipRole,
    directReports: globalState?.leaderData?.demographicSurveyData?.directReports,
    terminationDate: globalState?.leaderData?.demographicSurveyData?.terminationDate,
    orgSizeUnderLeader: globalState?.leaderData?.demographicSurveyData?.orgSizeUnderLeader,
    leaderProfileId: globalState?.leaderData?.id,
    jobLevelOptions,
    yearsAtJobLevelOptions,
    yearsInLeadershipRoleOptions,
    directReportsOptions,
    orgSizeUnderLeaderOptions,
    jobFunctionOptions,
    showFields: roleShowFields,
    anyDemographicsEnabled,
    demographicAcknowledged: globalState?.demographicAcknowledged,
  };
  /**
   * Company step props configuration
   * 
   * Collects all the props needed for the Company component,
   * including pre-filled data and dropdown options.
   */
  const companyShowFields = {
    showIndustry: checkShowFields(clientSurveyData?.industry),
    showIndustrySector: checkShowFields(clientSurveyData?.industrySector),
    showOrgType: checkShowFields(clientSurveyData?.orgType),
    showOrgStructure: checkShowFields(clientSurveyData?.orgStructure),
    showOrgSize: checkShowFields(clientSurveyData?.orgSize),
    showOrgCycle: checkShowFields(clientSurveyData?.orgCycle),
    showOrgRevenue: checkShowFields(clientSurveyData?.orgRevenue),
    showWorkingEnvironment: checkShowFields(clientSurveyData?.workingEnvironment),
  };

  const isCompanyStepVisible = hasAnyVisibleFields(companyShowFields);

  const companyProps = {
    industry: globalState?.leaderData?.demographicSurveyData?.industry,
    industrySector: globalState?.leaderData?.demographicSurveyData?.industrySector,
    orgType: globalState?.leaderData?.demographicSurveyData?.orgType,
    orgStructure: globalState?.leaderData?.demographicSurveyData?.orgStructure,
    orgSize: globalState?.leaderData?.demographicSurveyData?.orgSize,
    orgCycle: globalState?.leaderData?.demographicSurveyData?.orgCycle,
    orgRevenue: globalState?.leaderData?.demographicSurveyData?.orgRevenue,
    workingEnvironment: globalState?.leaderData?.demographicSurveyData?.workingEnvironment,
    leaderProfileId: globalState?.leaderData?.id,
    orgTypeOptions,
    orgStructureOptions,
    orgSizeOptions,
    orgCycleOptions,
    orgRevenueOptions,
    workingEnvironmentOptions,
    industryOptions,
    showFields: companyShowFields,
    anyDemographicsEnabled,
    demographicAcknowledged: globalState?.demographicAcknowledged,
  };

  /**
   * Personal step props configuration
   * 
   * Collects all the props needed for the Personal component,
   * including pre-filled data and dropdown options.
   */
  const personalShowFields = {
    showGender: checkShowFields(clientSurveyData?.gender),
    showCountryOfResidence: checkShowFields(clientSurveyData?.countryOfResidence),
    showEthnicity: checkShowFields(clientSurveyData?.ethnicity),
    showAge: checkShowFields(clientSurveyData?.age),
    showEducation: checkShowFields(clientSurveyData?.education),
    showLgbtq: checkShowFields(clientSurveyData?.lgbtq),
    showDiversityNonSpecific: checkShowFields(clientSurveyData?.diversityNonSpecific),
    showVeteranStatus: checkShowFields(clientSurveyData?.disabilityStatus),
  };

  const isPersonalStepVisible = hasAnyVisibleFields(personalShowFields);

  const personalProps = {
    gender: globalState?.leaderData?.demographicSurveyData?.gender,
    ethnicity: globalState?.leaderData?.demographicSurveyData?.ethnicity,
    lgbtq: globalState?.leaderData?.demographicSurveyData?.lgbtq,
    diversityNonSpecific: globalState?.leaderData?.demographicSurveyData?.diversityNonSpecific,
    veteranStatus: globalState?.leaderData?.demographicSurveyData?.disabilityStatus,
    education: globalState?.leaderData?.demographicSurveyData?.education,
    age: globalState?.leaderData?.demographicSurveyData?.age,
    countryOfResidence: globalState?.leaderData?.demographicSurveyData?.countryOfResidence,
    leaderProfileId: globalState?.leaderData?.id,
    timeZone: globalState?.leaderData?.demographicSurveyData?.timeZone,
    countryOfResidenceOptions,
    genderOptions,
    lgbtqOptions,
    diversityNonSpecificOptions,
    veteranStatusOptions,
    educationOptions,
    ageOptions,
    ethnicityOptions,
    showFields: personalShowFields,
    anyDemographicsEnabled,
    demographicAcknowledged: globalState?.demographicAcknowledged,
  };

  /**
   * Checks if all required fields in an object are present
   * 
   * Used to determine if a step is complete by checking if all
   * required fields have values in the leader data object.
   * 
   * @param {Object} obj - Object to check (usually leaderData)
   * @param {Array} fields - Array of field paths to check
   * @returns {boolean} True if all fields are present, false otherwise
   */
 
  const filteredPersonalFields = filterFields('personalFields', globalState);

  // Add conditional fields for certain countries
  if (
    globalState?.leaderData?.demographicSurveyData?.countryOfResidence === 'United States of America (the)' ||
    globalState?.leaderData?.demographicSurveyData?.countryOfResidence === 'United Kingdom of Great Britain and Northern Ireland (the)'
  ) {
    filteredPersonalFields.push('demographicSurveyData.lgbtq');
    filteredPersonalFields.push('demographicSurveyData.diversityNonSpecific');
  }

  if (globalState?.leaderData?.demographicSurveyData?.countryOfResidence === 'United States of America (the)') {
    filteredPersonalFields.push('demographicSurveyData.veteranStatus');
  }

  /**
   * Gets the list of visible steps based on client configuration
   * 
   * Creates a dynamic list of steps that filters out any steps
   * that are hidden based on client survey configuration.
   * 
   * @returns {Array} Array of visible step objects with path and visibility
   */
  const getVisibleSteps = () => {
    const steps = [
      { path: 'register-account', isVisible: true }, // Account is always visible
      { path: 'register-role', isVisible: isRoleStepVisible },
      { path: 'register-company', isVisible: isCompanyStepVisible },
      { path: 'register-personal', isVisible: isPersonalStepVisible },
    ];
    
    return steps.filter(step => step.isVisible);
  };

  /**
   * Determines the active step index based on the current URL
   * 
   * Maps the current route path to the corresponding step index
   * in the stepper workflow, dynamically adjusting for hidden steps.
   * 
   * @returns {number} The zero-based index of the active step
   */
  const activeIndex = () => {
    const visibleSteps = getVisibleSteps();
    const currentPath = pathname.split('/')[1];
    
    const index = visibleSteps.findIndex(step => step.path === currentPath);
     if (index === -1 && visibleSteps.length > 0) {
        // Current URL does not match any visible step, redirect to the first visible step
        navigate(`/${visibleSteps[0].path}`, { replace: true });
        return 0;
     }
     return index;
  };

  /**
   * Renders the current step component based on the active index
   * 
   * Dynamically renders the appropriate component based on which step
   * is currently active in the filtered visible steps array.
   * 
   * @returns {JSX.Element|null} The active step component or null
   */
  const renderActiveStep = () => {
    const visibleSteps = getVisibleSteps();
    const currentStepPath = visibleSteps[activeIndex()]?.path;

    switch (currentStepPath) {
      case 'register-account':
        return <Account {...accountProps} isRoleStepVisible={isRoleStepVisible} isCompanyStepVisible={isCompanyStepVisible} isPersonalStepVisible={isPersonalStepVisible} activeIndex={activeIndex()} />;
      case 'register-role':
        return <Role {...roleProps} isRoleStepVisible={isRoleStepVisible} isCompanyStepVisible={isCompanyStepVisible} isPersonalStepVisible={isPersonalStepVisible} activeIndex={activeIndex()} />;
      case 'register-company':
        return <Company {...companyProps} isRoleStepVisible={isRoleStepVisible} isCompanyStepVisible={isCompanyStepVisible} isPersonalStepVisible={isPersonalStepVisible} activeIndex={activeIndex()} />;
      case 'register-personal':
        return <Personal {...personalProps} isRoleStepVisible={isRoleStepVisible} isCompanyStepVisible={isCompanyStepVisible} isPersonalStepVisible={isPersonalStepVisible} activeIndex={activeIndex()} />;
      default:
        return null;
    }
  };

  return (
    <div className='mainWrapperOnboarding' style={{ backgroundImage: `url(${leftPanelImg})`}}>
      {/* Loading indicator */}
      {showLoader && <Loader />}

      {/* Right panel with header, stepper, and content */}
      <div className='middleContentWrapper' style={{ borderLeftColor: primaryColor }}>
        {/* Client-branded header */}
        <Header title={title} headerLogo={headerLogo} />
        <hr className='headerSeparator'/>

        {renderActiveStep()}
        <Footer />
      </div>
    </div>
  );
};

export default Onboarding;
